import React from 'react';
import { Box, Paper, Typography, LinearProgress, CircularProgress } from '@mui/material';
import { NotificationMetrics } from '../../../types/notification-admin';

interface FunnelAnalysisProps {
  metrics: NotificationMetrics | null;
  loading?: boolean;
}

/**
 * FunnelAnalysis - Conversion funnel visualization
 * Shows the flow from sent → delivered → read → actioned
 */
const FunnelAnalysis: React.FC<FunnelAnalysisProps> = ({ metrics, loading = false }) => {
  if (loading) {
    return (
      <Paper sx={{ p: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (!metrics || metrics.totalSent === 0) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No funnel data available
        </Typography>
      </Paper>
    );
  }

  const stages = [
    {
      name: 'Sent',
      count: metrics.totalSent,
      percentage: 100,
      color: '#1976d2',
      description: 'Notifications created and sent',
    },
    {
      name: 'Delivered',
      count: metrics.totalDelivered,
      percentage: metrics.deliveryRate,
      color: '#2e7d32',
      description: 'Successfully delivered to users',
      dropoff: metrics.totalSent - metrics.totalDelivered,
    },
    {
      name: 'Read',
      count: metrics.totalRead,
      percentage: metrics.readRate,
      color: '#0288d1',
      description: 'Opened and viewed by users',
      dropoff: metrics.totalDelivered - metrics.totalRead,
    },
    {
      name: 'Actioned',
      count: metrics.totalActioned,
      percentage: metrics.actionRate,
      color: '#7b1fa2',
      description: 'Users clicked action buttons',
      dropoff: metrics.totalRead - metrics.totalActioned,
    },
  ];

  const getDropoffPercentage = (stage: typeof stages[0], prevStage?: typeof stages[0]): number => {
    if (!prevStage || !stage.dropoff) return 0;
    return (stage.dropoff / prevStage.count) * 100;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Conversion Funnel
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Track how users engage with notifications from delivery to action
      </Typography>

      <Box sx={{ mt: 4 }}>
        {stages.map((stage, index) => {
          const prevStage = index > 0 ? stages[index - 1] : undefined;
          const dropoffPercentage = getDropoffPercentage(stage, prevStage);

          return (
            <Box key={stage.name} sx={{ mb: 3 }}>
              {/* Stage Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {stage.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stage.description}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h6" fontWeight="bold">
                    {stage.count.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stage.percentage.toFixed(1)}% of sent
                  </Typography>
                </Box>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ position: 'relative' }}>
                <LinearProgress
                  variant="determinate"
                  value={stage.percentage}
                  sx={{
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#f5f5f5',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: stage.color,
                      borderRadius: 1,
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      color: stage.percentage > 30 ? '#ffffff' : '#000000',
                      textShadow: stage.percentage > 30 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                    }}
                  >
                    {stage.percentage.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>

              {/* Dropoff Information */}
              {stage.dropoff !== undefined && stage.dropoff > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    backgroundColor: '#fff3e0',
                    borderRadius: 1,
                    border: '1px solid #ffe0b2',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    <strong>{stage.dropoff.toLocaleString()}</strong> users dropped off at this stage
                    ({dropoffPercentage.toFixed(1)}% of {prevStage?.name.toLowerCase()})
                  </Typography>
                </Box>
              )}

              {/* Connector Arrow */}
              {index < stages.length - 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    my: 2,
                  }}
                >
                  <Typography variant="h5" color="text.secondary">
                    ↓
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Summary Statistics */}
      <Box
        sx={{
          mt: 4,
          pt: 3,
          borderTop: '2px solid #e0e0e0',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 2,
        }}
      >
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Delivery Success
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="success.main">
            {metrics.deliveryRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Read Conversion
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="info.main">
            {metrics.readRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Action Conversion
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="secondary.main">
            {metrics.actionRate.toFixed(1)}%
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Overall Effectiveness
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={metrics.actionRate > 20 ? 'success.main' : 'warning.main'}
          >
            {((metrics.totalActioned / metrics.totalSent) * 100).toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default FunnelAnalysis;
